import React from 'react';
import { Clock, CheckCircle, XCircle } from 'lucide-react';
import { VerificationRequest } from '../../../types/dashboard';

interface VerificationRequestsTableProps {
  requests: VerificationRequest[];
  isLoading?: boolean;
  onRowClick: (requestId: string) => void;
}

const VerificationRequestsTable: React.FC<VerificationRequestsTableProps> = ({ 
  requests, 
  isLoading, 
  onRowClick 
}) => {
  if (isLoading) {
    return (
      <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
        <div className="h-5 bg-gray-800 rounded w-48 mb-4 animate-pulse" />
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 bg-gray-800 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
        <h3 className="text-white text-lg font-semibold mb-4">Recent Verification Requests</h3>
        <div className="text-center py-8">
          <CheckCircle size={48} className="text-gray-700 mx-auto mb-3" />
          <p className="text-gray-400">No verification requests</p>
        </div>
      </div>
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle size={16} className="text-[#23F47C]" />;
      case 'rejected':
        return <XCircle size={16} className="text-red-500" />;
      default:
        return <Clock size={16} className="text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'text-[#23F47C] bg-[#23F47C] bg-opacity-10';
      case 'rejected':
        return 'text-red-500 bg-red-500 bg-opacity-10';
      default:
        return 'text-yellow-500 bg-yellow-500 bg-opacity-10';
    }
  };

  return (
    <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
      <h3 className="text-white text-lg font-semibold mb-4">Recent Verification Requests</h3>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="text-left text-gray-400 text-sm font-medium pb-3">Member</th>
              <th className="text-left text-gray-400 text-sm font-medium pb-3">Program</th>
              <th className="text-left text-gray-400 text-sm font-medium pb-3">Date</th>
              <th className="text-left text-gray-400 text-sm font-medium pb-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {requests.slice(0, 5).map((request) => (
              <tr
                key={request.id}
                onClick={() => onRowClick(request.id)}
                className="border-b border-gray-800 hover:bg-gray-800 cursor-pointer transition-colors"
              >
                <td className="py-4 text-white">{request.memberName}</td>
                <td className="py-4 text-gray-400">{request.programName}</td>
                <td className="py-4 text-gray-400 text-sm">
                  {new Date(request.requestDate).toLocaleDateString()}
                </td>
                <td className="py-4">
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(request.status)}
                    <span className={`text-sm px-2 py-1 rounded ${getStatusColor(request.status)}`}>
                      {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VerificationRequestsTable;
